/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inferer, infer } from '../infer';

type Ctx = Record<string, any>;

export type ContextOptions = {
  /**
   * Option for shared context between procedure under the same context.
   * useful when context should be shared between procedure.
   *
   * When false, context will be isolated from each procedure and run when only needed.
   *
   * Default to true.
   */
  shared: boolean;
};

export type ContextValue<V = any, T extends Ctx = Ctx> = T & { $value: V };
export type ContextFunction<O, I extends any[], T extends Ctx> = (
  context: T,
  ...input: I
) => O;

/** Procedure hook on after run called */
export type OnAfterHook = (value: unknown) => void;

/** Procedure hook on before run called */
export type OnBeforeHook<I extends any[], T extends Ctx> = (
  context: T,
  input: I
) => void;

export type ProcedureOptions<I extends any[], T extends Record<string, any>> = {
  onBeforeCalled: OnBeforeHook<I, T>;
  onAfterCalled: OnAfterHook;
};

/** Garda context used for injecting context to procedure */
export class Context<T extends Ctx> {
  private _fn: () => T;
  private _context: null | T = null;
  private _updater: Set<(ctx: T) => void> = new Set();

  private shared: boolean;
  private procedureOpts: Partial<ProcedureOptions<unknown[], T>>;

  constructor(context: () => T, options: Partial<ContextOptions> = {}) {
    this._fn = context;
    const { shared } = options;
    this.procedureOpts = {};

    this.shared = !!shared;
    if (shared === true) {
      this._context = context();
    }
  }

  /** context generator depending on context state */
  private _caller() {
    // on shared context, context already initiated.
    if (this._context !== null) return this._context;

    // on isolated, context need to applied updater
    // before returning initiated context
    const ctx = this._fn();
    for (const fn of this._updater.values()) {
      fn(ctx);
    }

    return ctx;
  }

  /**
   * context function, alias for context()
   *
   * > ! notice. Different treatment on immidiate false/true.
   * >
   * > on immidiate option false (default), `context()` and `context.get()`
   * > will create a fresh context everytime. But when immidiate is set to true,
   * > context is already initiated. So it's not going to be isolated context.
   * */
  get() {
    return this._caller();
  }

  /**
   * context utility for set context value
   *
   * > ! notice. Different tretment on immidiate false/true.
   * >
   * > on immidiate option true, when set is called. context isolated feature will be
   * > terminated because context is set to object that already initiated. And every update
   * > on context and it's object that context set to will be immidiately applied.
   * >
   * > Isolated feature of the context can be set again by calling `context.reset()`,
   * > and context will be set to initial context value.
   */
  set(context: T) {
    this._context = context;
    this._updater.clear();
  }

  /**
   * context utility for updating context value
   *
   * > ! notice. Different tretment on immidiate false/true.
   * >
   * > on immidiate option false (default). Given update will be treated as a hook function
   * > that only will be called when procedure is called as needed because of the isolated treatment.
   * >
   * > But when isolated feature is terminated by setting context through `context.set()`,
   * > update will be applied immidiately.
   */
  update(fn: (context: T) => void) {
    if (this._context !== null) {
      fn(this._context);
    } else {
      this._updater.add(fn);
    }
  }

  /**
   * reset context to initial context initiation function.
   * this will remove all previous update to the context.
   * on immidiate set to true, context that is reseted will not point to previous initiated context.
   *
   * ```js
   * // Example
   * const context = createContext(() => {
   *  let count = 0
   *  return { setCount: (num) => count = num, count: () => count }
   * }, { immidiate: true })
   *
   * const ctx = context.get()
   *
   * ctx.setCount(10)
   * context().count() // this will return 10 too.
   *
   * context.update(c => c.setCount(20))
   * ctx.count() // this will return 20
   *
   * // on reset context
   * context.reset()
   * context().count() // will return 0
   * ctx.count() // will return 20
   *
   * ```
   */
  reset() {
    if (this.shared === true) {
      this._context = this._fn();
    } else {
      this._context = null;
      this._updater.clear();
    }
  }

  /** create procedure from context */
  procedure<O, I extends any[]>(fn: ContextFunction<O, I, T>) {
    const procedure = new Procedure(this, fn);
    if (typeof this.procedureOpts.onAfterCalled === 'function') {
      procedure.onAfterCalled(this.procedureOpts.onAfterCalled);
    }

    if (typeof this.procedureOpts.onBeforeCalled === 'function') {
      procedure.onBeforeCalled(this.procedureOpts.onBeforeCalled);
    }

    return procedure;
  }

  onAfterHook(fn: OnAfterHook) {
    this.procedureOpts.onAfterCalled = fn;
    return this;
  }

  onBeforeHook(fn: OnBeforeHook<unknown[], T>) {
    this.procedureOpts.onBeforeCalled = fn;
    return this;
  }
}

export class Procedure<O, I extends any[], T extends Ctx> {
  private _context: Context<T>;
  private _originalContext: Context<T>;
  private _rootFn: ContextFunction<O, I, T>;

  private _afterHookListener = new Set<OnAfterHook>();
  private _beforeHookListener = new Set<OnBeforeHook<I, T>>();

  private _runAfterListener(value: unknown) {
    for (const fn of this._afterHookListener.values()) {
      fn(value);
    }
  }

  private _runBeforeListener(context: T, input: I) {
    for (const fn of this._beforeHookListener.values()) {
      fn(context, input);
    }
  }

  constructor(context: Context<T>, fn: ContextFunction<O, I, T>) {
    this._originalContext = context;
    this._context = context;
    this._rootFn = fn;
  }

  /** Execute procedure function */
  get run() {
    return (...input: I): O => {
      const context = this._context.get();
      this._runBeforeListener(context, input);
      const value = this._rootFn(context, ...input);
      this._runAfterListener(value);
      return value;
    };
  }

  /**
   * Utility function for transforming and processing output from procedure.
   *
   * ---
   *
   * ```js
   *
   * // Usage
   * const getCount = context
   *  .procedure((ctx) => ctx.count)
   *
   * const doubleCount = getCount.pipe((value) => value * 2)
   * const isCountEven = getCount.pipe((value) => value % 2)
   *
   * const count = getCount() // return 1
   * const doubled = doubleCount() // return 2
   * const isEven = isCountEven() // return true
   *
   * ```
   */
  get pipe() {
    type ProcedurePipe<Out, In extends any[]> = ((...input: In) => Out) & {
      run: (...input: In) => Out;
      pipe: <V>(fn: (value: Out) => V) => ProcedurePipe<V, In>;
    };

    const generator = <Prev, Next>(
      prevFn: (...input: I) => Prev,
      nextFn: (value: Prev) => Next
    ): ProcedurePipe<Next, I> => {
      const runner = (...input: I) => {
        const prev = prevFn(...input);
        const value = nextFn(prev);
        this._runAfterListener(value);
        return value;
      };

      return Object.assign(runner, {
        run: runner,
        pipe<V>(fn: (value: Next) => V) {
          return generator((...input: I) => {
            const value = prevFn(...input);
            return nextFn(value);
          }, fn) as ProcedurePipe<V, I>;
        },
      }) as ProcedurePipe<Next, I>;
    };

    const runner = (...input: I): O => {
      const context = this._context.get();
      this._runBeforeListener(context, input);
      return this._rootFn(context, ...input);
    };

    return <V>(fn: (value: O) => V) => {
      return generator(runner, fn) as ProcedurePipe<V, I>;
    };
  }

  /**
   * Utility for injecting updated context.
   *
   * useful when context is globally stored and updated,
   * but procedure need to be isolated.
   *
   * ---
   *
   * ```js
   *
   * // Example
   *
   * // isolated get data procedure with fetch
   * const getData = context
   *  .procedure((ctx) => fetch('/some-api', {
   *    headers: { "Authorization": `Bearer ${ctx.token}` }
   *  }))
   *
   * // get context and set token from cookie
   * const ctx = context()
   * ctx.token = cookie.get('Access-Token')
   * getData.inject(ctx) // inject updated context
   * const data = await getData()
   *
   * // or using context update utility
   * context.update((ctx) => {
   *  ctx.token = cookie.get('Access-Token)
   *  return ctx
   * })
   *
   * getData.inject(context)
   *
   * // or even better, directly update procedure context
   * getData.inject(ctx => {
   *  ctx.token = cookie.get('Access-Token)
   *  return ctx
   * })
   * ```
   */
  inject(context: T | Context<T> | ((context: T) => T)) {
    // treatment for context is an context update function
    if (typeof context === 'function') {
      const ctx = this._context.get();
      this._context = new Context(() => context(ctx));
      return;
    }

    // treatment for context as context object
    if (context instanceof Context) {
      this._context = context;
      return;
    }

    // treatment for context as actual context
    this._context = new Context(() => context);
  }

  /**
   * Utility for reseting context that has been injected
   * to original context used in current procedure.
   */
  resetContext() {
    this._context = this._originalContext;
  }

  onBeforeCalled(fn: OnBeforeHook<I, T>) {
    this._beforeHookListener.add(fn);
    return () => {
      this._beforeHookListener.delete(fn);
    };
  }

  onAfterCalled(fn: OnAfterHook) {
    this._afterHookListener.add(fn);
    return () => {
      this._afterHookListener.delete(fn);
    };
  }

  /**
   * Utility function for transforming and processing output from procedure.
   * Different than pipe utility, this utility give you context and input value.
   *
   * ---
   *
   * ```js
   *
   * // Usage
   * const getCount = sharedContext
   *  .procedure((ctx) => ctx.count)
   *
   * const doubleCount = getCount.use((ctx) => ctx.$value * 2)
   * const isCountEven = getCount.use((ctx) => ctx.count % 2)
   *
   * const count = getCount() // return 1
   * const doubled = doubleCount() // return 2
   * const isEven = isCountEven() // return true
   *
   * ```
   */
  use<V>(fn: ContextFunction<V, I, ContextValue<O, T>>) {
    const wrapFn = (context: T, ...input: I) => {
      const value = this._rootFn(context, ...input);
      return fn({ ...context, $value: value }, ...input);
    };

    return new Procedure(this._context, wrapFn);
  }

  /**
   * Utility used for asyncronous return from procedure.
   * best use for data validation after fetching data using any data validation
   * like zod or any other data validation library.
   *
   * ---
   *
   * ```js
   *
   * // Example
   *
   * // isolated get data procedure with fetch
   * const getData = context
   *  .procedure((ctx) => fetch('/some-api', {
   *    headers: { "Authorization": `Bearer ${ctx.token}` }
   *  }))
   *  .useAsync(({ $value }) => {
   *    if (typeof $value !== 'object') return undefined
   *    return $value
   *  })
   *
   * // run getData procedure
   * const result = getData().then(value => {
   *  console.log(value) // -> value should be object or undefined
   * })
   * ```
   */
  useAsync<V>(fn: ContextFunction<V, I, ContextValue<Awaited<O>, T>>) {
    const wrapFn = async (context: T, ...input: I) => {
      const value = await this._rootFn(context, ...input);
      return await fn({ ...context, $value: value }, ...input);
    };

    return new Procedure(this._context, wrapFn);
  }

  /**
   * Utility function for infering output and transform to new output type.
   *
   * ---
   *
   * ```ts
   *
   * // Usage
   * const getUserState = context
   *  .procedure((ctx, key: string) => ctx.getLocalStorageState(key))
   *  .infer(i => ({
   *    status: i.str('user.status.label'),
   *    hasWife: i.str('user.partner.gender') === 'female',
   *    hasChild: i.num('user.children.count') > 0
   *  }))
   *
   * const state = getUserState('some-key')
   *
   * ```
   */
  infer<V>(fn: (inferer: Inferer) => V) {
    const wrapFn = (context: T, ...input: I) => {
      const value = this._rootFn(context, ...input);
      return fn(infer(value));
    };

    return new Procedure(this._context, wrapFn);
  }

  /**
   * Utility function for infering output and transform to new output type.
   * Equal to infer but for handling asyncronous output.
   *
   * ---
   *
   * ```ts
   *
   * // Usage
   * const getUserName = context
   *  .procedure(async (ctx, id: string) => await ctx.fetchUserData(id))
   *  .inferAsync(i =>
   *    i.str('data.fullName') + ' ' + i.str('data.lastName')
   *  )
   *
   * const name = await getUserName('some-user-id')
   *
   * ```
   */
  inferAsync<V>(fn: (inferer: Inferer) => V) {
    const wrapFn = async (context: T, ...input: I) => {
      const value = await this._rootFn(context, ...input);
      return await fn(infer(value));
    };

    return new Procedure(this._context, wrapFn);
  }
}
